import { getConfig } from '../../../config'

/**
 * Determines whether a given URL is considered internal to the application.
 *
 * A URL is considered internal if:
 * - It's a hash fragment (starts with '#')
 * - It's a relative path (starts with '/')
 * - It's an absolute URL whose hostname matches the configured main domain or is a subdomain of it
 * - It's an invalid URL that can't be parsed (assumed to be internal for safety)
 *
 * @param href - The URL to check. Can be undefined or null.
 * @returns True if the URL is considered internal, false otherwise.
 *
 * @example
 * ```typescript
 * isInternalUrl('#section')           // true - hash fragment
 * isInternalUrl('/about')             // true - relative path
 * isInternalUrl('https://superhuman.com/pricing')  // true - matches main domain
 * isInternalUrl('https://app.superhuman.com')      // true - contains main domain
 * isInternalUrl('https://google.com') // false - external domain
 * isInternalUrl(undefined)            // false - no URL provided
 * isInternalUrl('invalid-url')        // true - parsing fails, assumed internal
 * ```
 */
export function isInternalUrl(href: string | undefined): boolean {
  if (!href) return false

  if (href.startsWith('#') || href.startsWith('/')) return true

  try {
    const url = new URL(href)
    const { domains } = getConfig()
    return url.hostname === domains.main || url.hostname.includes(`.${domains.main}`)
  } catch {
    // If URL parsing fails, it's likely an internal URL
    return true
  }
}
